/*--------------------------------------------------------------
# Custom scripts for Card grid block
--------------------------------------------------------------*/

/** Selector for all block containers that may contain buttons to wrap. Single query scope. */
const BUTTON_WRAP_CONTAINER_SELECTOR = '.card-grid, .columns-cards, .cta-columns-2, .text-image-alt, .text-video, .text-image, .animated-read, .text-accordion, .timeline, .text-columns';

/** Root to limit queries (main content area). Falls back to document.body if #content not present. */
function getButtonWrapRoot() {
    return document.querySelector('#content') || document.body;
}

/**
 * Process a single block container: find button areas and run wrapButtonsInContainer.
 * Branches on container classList instead of separate queries.
 * @param {HTMLElement} container - One of the block types in BUTTON_WRAP_CONTAINER_SELECTOR
 */
function processButtonWrapContainer(container) {
    const c = container.classList;

    if (c.contains('card-grid') || c.contains('columns-cards') || c.contains('cta-columns-2')) {
        const cardSelector = c.contains('columns-cards') ? '.card--simple' : c.contains('cta-columns-2') ? '.card--cta' : '.card';
        const cards = Array.from(container.querySelectorAll(cardSelector))
            .filter(card => !c.contains('card-grid') ? true : !card.classList.contains('card--complex'));
        cards.forEach(card => {
            const cardBody = card.querySelector('.card-body');
            if (!cardBody) return;
            const target = c.contains('cta-columns-2') && cardBody.querySelector('.acf-innerblocks-container')
                ? cardBody.querySelector('.acf-innerblocks-container')
                : cardBody;
            wrapButtonsInContainer(target);
        });
        return;
    }

    if (c.contains('text-image-alt') || c.contains('text-video') || c.contains('text-image')) {
        container.querySelectorAll('.column-content, .text-content').forEach(wrapButtonsInContainer);
        return;
    }

    if (c.contains('animated-read')) {
        container.querySelectorAll('.card--animated-read .card-body').forEach(wrapButtonsInContainer);
        container.querySelectorAll('.text-scroll-card').forEach(wrapButtonsInContainer);
        return;
    }

    if (c.contains('text-accordion')) {
        container.querySelectorAll('.accordion-body').forEach(wrapButtonsInContainer);
        return;
    }

    if (c.contains('timeline')) {
        container.querySelectorAll('.card-body').forEach(wrapButtonsInContainer);
        return;
    }

    if (c.contains('text-columns')) {
        container.querySelectorAll('.column-content').forEach(wrapButtonsInContainer);
    }
}

/**
 * Wrap buttons in .d-flex.button-wrap where two or more are consecutive.
 * Runs within an optional root to limit DOM scope; defaults to #content or document.body.
 * @param {HTMLElement} [root] - Optional. Only process containers inside this element. If omitted, uses getButtonWrapRoot().
 */
function wrapCardButtons(root) {
    const scope = root || getButtonWrapRoot();
    const containers = scope.matches && scope.matches(BUTTON_WRAP_CONTAINER_SELECTOR)
        ? [scope, ...scope.querySelectorAll(BUTTON_WRAP_CONTAINER_SELECTOR)]
        : scope.querySelectorAll(BUTTON_WRAP_CONTAINER_SELECTOR);

    Array.from(containers).forEach(processButtonWrapContainer);
}

/**
 * Wrap buttons only when two or more are consecutive siblings (no other elements between them).
 * Single buttons or buttons separated by other content (e.g. in Text blocks) are left as default.
 * @param {HTMLElement} container - The container element to search for buttons
 */
function wrapButtonsInContainer(container) {
    const buttonSelector = '.btn-link, .btn-link--secondary, .btn-text-link';
    const buttons = Array.from(container.querySelectorAll(buttonSelector))
        .filter(btn => !btn.closest('.button-wrap'));

    // Group buttons by their direct parent
    const buttonsByParent = new Map();
    buttons.forEach(btn => {
        const parent = btn.parentElement;
        if (!buttonsByParent.has(parent)) {
            buttonsByParent.set(parent, []);
        }
        buttonsByParent.get(parent).push(btn);
    });

    buttonsByParent.forEach((siblingButtons, parent) => {
        if (siblingButtons.length < 2) {
            return;
        }
        if (parent.classList.contains('d-flex') && parent.classList.contains('button-wrap')) {
            return;
        }

        // Find runs of consecutive button siblings (no other elements between them)
        const buttonSet = new Set(siblingButtons);
        const children = Array.from(parent.children);
        const runs = [];
        let currentRun = [];

        children.forEach(child => {
            if (buttonSet.has(child)) {
                currentRun.push(child);
            } else {
                if (currentRun.length >= 2) {
                    runs.push(currentRun);
                }
                currentRun = [];
            }
        });
        if (currentRun.length >= 2) {
            runs.push(currentRun);
        }

        // Wrap each run of consecutive buttons
        runs.forEach((run) => {
            const wrapper = document.createElement('div');
            wrapper.className = 'd-flex button-wrap';
            parent.insertBefore(wrapper, run[0]);
            run.forEach(button => {
                wrapper.appendChild(button);
            });
        });
    });
}

// Public API: call after inserting dynamic content so buttons are wrapped in that subtree only.
// e.g. window.burgesSalmonWrapButtons(insertedElement)
window.burgesSalmonWrapButtons = function (container) {
    if (container && container.nodeType === Node.ELEMENT_NODE) {
        wrapCardButtons(container);
    } else {
        wrapCardButtons();
    }
};

// Frontend only: skip in wp-admin so no work or listeners run there.
function initButtonWrap() {
    if (document.body && document.body.classList.contains('wp-admin')) return;
    wrapCardButtons();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initButtonWrap);
} else {
    initButtonWrap();
}